import React from 'react';
import { FormControl, FormGroup, FormLabel } from '@mui/material';

import { assertExhaustiveSwitch } from '@paragon/utilities';

import {
  PointSnappingSettings,
  PointSnapToggleKey,
} from '../../../../../library/layout-snapping/point-snapping/settings';
import { layoutSlice, PointSnappingContext } from '../../../../../store/slices/layoutSlice';
import { useAppDispatch } from '../../../../hooks';
import { SnappingSettingsCheckbox } from './SnappingSettingsCheckbox';

export function PointSnappingSettingsControls(props: {
  context: PointSnappingContext;
  settings: PointSnappingSettings;
  disabledSnaps?: Partial<Record<PointSnapToggleKey, string>>;
}) {
  const dispatch = useAppDispatch();
  const { context, settings, disabledSnaps } = props;

  const contextualSegmentTerm = getContextualSegmentTerm(context);

  const angleDisabledReason =
    disabledSnaps?.angle ?? (settings.segmentSnaps.orthogonal ? 'Not compatible with Orthogonal snapping.' : null);

  return (
    <FormControl>
      <FormLabel>Active Snaps</FormLabel>
      <FormGroup>
        <SnappingSettingsCheckbox
          label="Intersection"
          tooltip={disabledSnaps?.intersections ?? 'Snap to intersections of active items.'}
          disabled={disabledSnaps?.intersections != null}
          checked={settings.entitySnaps.intersections}
          onChange={() => dispatch(layoutSlice.actions.pointSnappingIntersectionsToggled({ context }))}
        />

        <SnappingSettingsCheckbox
          label="Extension"
          tooltip={
            disabledSnaps?.extensionIntersections ?? 'Snap to intersections of lines extended from active items.'
          }
          disabled={disabledSnaps?.extensionIntersections != null}
          checked={settings.entitySnaps.extensionIntersections}
          onChange={() => dispatch(layoutSlice.actions.pointSnappingExtensionIntersectionsToggled({ context }))}
        />

        <SnappingSettingsCheckbox
          label="Midpoint"
          tooltip={disabledSnaps?.midpoints ?? 'Snap to midpoints of active segments.'}
          disabled={disabledSnaps?.midpoints != null}
          checked={settings.entitySnaps.midpoints}
          onChange={() => dispatch(layoutSlice.actions.pointSnappingMidpointsToggled({ context }))}
        />

        <SnappingSettingsCheckbox
          label="Endpoint"
          tooltip={disabledSnaps?.endpoints ?? 'Snap to endpoints of active segments.'}
          disabled={disabledSnaps?.endpoints != null}
          checked={settings.entitySnaps.endpoints}
          onChange={() => dispatch(layoutSlice.actions.pointSnappingEndpointsToggled({ context }))}
        />

        <SnappingSettingsCheckbox
          label="Nearest"
          tooltip={disabledSnaps?.nearestPoint ?? 'Snap to the nearest point on an active item.'}
          disabled={disabledSnaps?.nearestPoint != null}
          checked={settings.entitySnaps.nearestPoint}
          onChange={() => dispatch(layoutSlice.actions.pointSnappingNearestPointToggled({ context }))}
        />

        <SnappingSettingsCheckbox
          label="Perpendicular"
          tooltip={
            disabledSnaps?.perpendicularProjection ??
            `Snap the ${contextualSegmentTerm} if perpendicular to the hovered item.`
          }
          disabled={disabledSnaps?.perpendicularProjection != null}
          checked={settings.entitySnaps.perpendicularProjection}
          onChange={() => dispatch(layoutSlice.actions.pointSnappingPerpendicularProjectionToggled({ context }))}
        />

        <SnappingSettingsCheckbox
          label="Orthogonal"
          tooltip={disabledSnaps?.orthogonal ?? `Snap the ${contextualSegmentTerm} to vertical or horizontal.`}
          disabled={disabledSnaps?.orthogonal != null}
          checked={settings.segmentSnaps.orthogonal}
          onChange={() => dispatch(layoutSlice.actions.pointSnappingOrthogonalToggled({ context }))}
        />

        <SnappingSettingsCheckbox
          label="Angle"
          tooltip={angleDisabledReason ?? `Snap the ${contextualSegmentTerm} to a whole-degree angle.`}
          disabled={angleDisabledReason != null}
          checked={settings.segmentSnaps.angle}
          onChange={() => dispatch(layoutSlice.actions.pointSnappingAngleToggled({ context }))}
        />

        <SnappingSettingsCheckbox
          label="Distance"
          tooltip={
            disabledSnaps?.lengthIncrement ?? `Snap the ${contextualSegmentTerm} length to feet, inches, or ¼ inches.`
          }
          disabled={disabledSnaps?.lengthIncrement != null}
          checked={settings.segmentSnaps.lengthIncrement}
          onChange={() => dispatch(layoutSlice.actions.pointSnappingLengthIncrementToggled({ context }))}
        />
      </FormGroup>
    </FormControl>
  );
}

function getContextualSegmentTerm(context: PointSnappingContext): string {
  switch (context) {
    case 'bearingEnvelope':
    case 'trussEnvelope':
      return 'envelope';

    case 'general':
      return 'segment';

    default:
      assertExhaustiveSwitch(context);
  }
}
